import { makeStyles } from '@material-ui/core/styles';
import { Card, CardActionArea, CardContent, Typography, Chip, Box } from '@material-ui/core';
import LaunchIcon from '@material-ui/icons/Launch';
import { ExternalArticle } from '../data/externalArticles';

const useStyles = makeStyles(theme => ({
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[6],
    },
  },
  cardAction: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  content: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  source: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  externalIcon: {
    fontSize: 16,
    color: theme.palette.text.secondary,
  },
  title: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    lineHeight: 1.3,
  },
  author: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    marginBottom: theme.spacing(1),
  },
  excerpt: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    flexGrow: 1,
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    marginBottom: theme.spacing(1),
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginTop: theme.spacing(2),
  },
  tags: {
    display: 'flex',
    gap: theme.spacing(0.5),
    flexWrap: 'wrap',
    flex: 1,
  },
  tag: {
    height: 24,
    fontSize: '0.7rem',
  },
  date: {
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
    whiteSpace: 'nowrap',
    marginLeft: theme.spacing(1),
  },
}));

interface ExternalArticleCardProps {
  article: ExternalArticle;
}

export const ExternalArticleCard = ({ article }: ExternalArticleCardProps) => {
  const classes = useStyles();

  return (
    <Card className={classes.card}>
      <CardActionArea
        className={classes.cardAction}
        component="a"
        href={article.url}
        target="_blank"
        rel="noopener noreferrer"
      >
        <CardContent className={classes.content}>
          <Box className={classes.header}>
            <Typography className={classes.source}>{article.source}</Typography>
            <LaunchIcon className={classes.externalIcon} />
          </Box>
          <Typography variant="h6" className={classes.title}>
            {article.title}
          </Typography>
          {article.author && (
            <Typography className={classes.author}>by {article.author}</Typography>
          )}
          <Typography className={classes.excerpt}>{article.excerpt}</Typography>
          <Box className={classes.footer}>
            {article.tags && article.tags.length > 0 && (
              <Box className={classes.tags}>
                {article.tags.map(tag => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    className={classes.tag}
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
            <Typography className={classes.date}>{article.date}</Typography>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};
